// app/javascript/controllers/related_modal_controller.js
//
// "Postuler" opens the employer's site in a new tab. When the visitor comes
// back to this tab, show the related offers once, in a dialog they can close
// to get back to the job.
//
// State lives in sessionStorage so it survives the browser discarding and
// reloading this tab while the visitor is away (common on iOS).
import { Controller } from "@hotwired/stimulus"

const STORAGE_KEY = "jobmed:related-modal"
const MAX_SHOWS_PER_SESSION = 2

export default class extends Controller {
  static targets = ["dialog"]
  static values = {
    jobId: Number,
    // Coming back faster than this is a misclick, not an application.
    minAwayMs: { type: Number, default: 5000 },
    // Development only: open immediately to review the design.
    preview: Boolean,
  }

  connect() {
    this.onVisibilityChange = this.onVisibilityChange.bind(this)
    document.addEventListener("visibilitychange", this.onVisibilityChange)

    if (this.previewValue) {
      this.open()
    } else if (document.visibilityState === "visible") {
      // The tab may have been reloaded while the visitor was away.
      this.maybeOpen()
    }
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
    this.unlockScroll()
  }

  // click->related-modal#markApplied on the "Postuler" links
  markApplied() {
    const state = this.readState()
    state.pending = { jobId: this.jobIdValue, at: Date.now() }
    this.writeState(state)
  }

  onVisibilityChange() {
    if (document.visibilityState === "visible") this.maybeOpen()
  }

  maybeOpen() {
    const state = this.readState()
    const pending = state.pending
    if (!pending || pending.jobId !== this.jobIdValue) return

    const awayMs = Date.now() - pending.at
    if (awayMs < this.minAwayMsValue) return

    delete state.pending
    const shownJobs = state.shownJobs || []
    if (shownJobs.includes(this.jobIdValue) || shownJobs.length >= MAX_SHOWS_PER_SESSION) {
      this.writeState(state)
      return
    }

    state.shownJobs = [...shownJobs, this.jobIdValue]
    this.writeState(state)
    this.open()
  }

  open() {
    if (this.dialogTarget.open) return

    this.lockScroll()
    this.dialogTarget.showModal()
    this.dialogTarget.addEventListener("close", () => this.onClosed(), { once: true })
    this.track("related_modal_shown")
  }

  // "Revenir à l'offre", the × button
  close() {
    this.dismissedBy = "button"
    this.dialogTarget.close()
  }

  // click->related-modal#backdropClose on the <dialog>: a click whose target
  // is the dialog itself landed on the backdrop, outside the panel.
  backdropClose(event) {
    if (event.target !== this.dialogTarget) return
    this.dismissedBy = "backdrop"
    this.dialogTarget.close()
  }

  // click->related-modal#trackJobClick on each offer card
  trackJobClick() {
    this.clickedJob = true
    this.track("related_modal_job_click")
  }

  onClosed() {
    this.unlockScroll()
    // Esc closes the dialog natively, without going through close().
    if (!this.clickedJob) this.track("related_modal_dismiss", this.dismissedBy || "escape")
    this.dismissedBy = null
  }

  lockScroll() {
    this.scrollY = window.scrollY
    document.body.style.position = "fixed"
    document.body.style.top = `-${this.scrollY}px`
    document.body.style.width = "100%"
    this.scrollLocked = true
  }

  unlockScroll() {
    if (!this.scrollLocked) return

    document.body.style.position = ""
    document.body.style.top = ""
    document.body.style.width = ""
    window.scrollTo(0, this.scrollY)
    this.scrollLocked = false
  }

  // Clarity custom events, filterable under Filters > Custom events. The
  // dismiss method is also set as a session tag.
  track(name, dismissedBy = null) {
    if (typeof window.clarity !== "function") return

    window.clarity("event", name)
    if (dismissedBy) window.clarity("set", "related_modal_dismiss", dismissedBy)
  }

  readState() {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {}
    } catch {
      return {}
    }
  }

  writeState(state) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch {
      // Private mode or storage disabled: the modal just never shows.
    }
  }
}
